// Costos del inventario — Inventario → Reportes → Costos.
//
// Dos preguntas que el promedio ponderado no sabe responder solo:
//
// 1. «De estas 400 unidades, ¿cuántas costaron 1,20 y cuántas 1,45?» — las
//    capas de costo. Es el dato que hace falta para poner el precio de venta y
//    para discutir la siguiente compra con el proveedor.
// 2. «¿Qué hay en bodega que el balance está contando como si no valiera
//    nada?» — la cola de pendientes de costear.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calculator, Layers, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  BuscadorProducto, Campo, Location, ProductoOpt, SortTh, fieldCls, invRpc,
  locationsList, money, num, productosOptList, useSort,
} from './shared';
import { ModalCorregirCosto } from './ModalCorregirCosto';
import { LOCALE } from '@/lib/utils/regional';

// ---------------------------------------------------------------------------
// Capas de costo
// ---------------------------------------------------------------------------
interface Capa {
  id: string; fecha: string; location_id: string; bodega: string | null;
  unidades: number; restantes: number; costo_unitario: number; valor: number;
  origen: string | null; documento: string | null; proveedor: string | null; numero_lote: string | null;
}
interface ResumenCapas {
  unidades: number; valor_capas: number; costo_min: number; costo_max: number;
  costo_capas: number; unidades_stock: number; valor_promedio: number;
}

function Capas() {
  const [prod, setProd] = useState('');
  const [loc, setLoc] = useState('');
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const { data, isFetching } = useQuery<{ resumen: ResumenCapas | null; capas: Capa[] }>({
    queryKey: ['inv-capas', prod, loc],
    queryFn: () => invRpc('inv_capas', { _producto: prod, _location: loc || null }),
    enabled: !!prod,
  });
  const r = data?.resumen;
  const capas = data?.capas ?? [];
  // La brecha entre lo que dice el promedio y lo que realmente queda. No es un
  // error: es la medida de lo que el método está escondiendo.
  const brecha = r ? Number(r.valor_capas) - Number(r.valor_promedio) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Campo label="Producto" className="w-96">
          <BuscadorProducto value={prod} onChange={setProd} productos={productos} allowClear />
        </Campo>
        <Campo label="Bodega" className="w-56">
          <select className={fieldCls} value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value="">Todas mis bodegas</option>
            {bodegas.filter((b) => b.es_activo).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </Campo>
      </div>

      {!prod && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          Elige un producto para ver de qué compra salió cada unidad que queda en bodega.
        </p>
      )}

      {r && (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Card><CardContent className="pt-4">
              <span className="block text-xs text-muted-foreground">En bodega</span>
              <span className="text-xl font-semibold tabular-nums">{num(r.unidades_stock)}</span>
              {Number(r.unidades) !== Number(r.unidades_stock) && (
                <span className="block text-[11px] text-amber-700 dark:text-amber-400">
                  {num(r.unidades)} con capa conocida
                </span>)}
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <span className="block text-xs text-muted-foreground">Costo real de lo que queda</span>
              <span className="text-xl font-semibold tabular-nums">{num(r.costo_capas, 4)}</span>
              <span className="block text-[11px] text-muted-foreground">
                entre {num(r.costo_min, 4)} y {num(r.costo_max, 4)}
              </span>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <span className="block text-xs text-muted-foreground">Valor por capas</span>
              <span className="text-xl font-semibold tabular-nums">{money(r.valor_capas)}</span>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <span className="block text-xs text-muted-foreground">Diferencia con el promedio</span>
              <span className={`text-xl font-semibold tabular-nums ${Math.abs(brecha) < 0.01 ? '' : brecha > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {money(brecha)}
              </span>
              <span className="block text-[11px] text-muted-foreground">balance: {money(r.valor_promedio)}</span>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Capas vivas ({capas.length})</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Entró</th><th className="py-2 pr-3">Bodega</th>
                    <th className="py-2 pr-3">Documento</th><th className="py-2 pr-3">Proveedor</th>
                    <th className="py-2 pr-3">Lote</th>
                    <th className="py-2 pr-3 text-right">Entraron</th><th className="py-2 pr-3 text-right">Quedan</th>
                    <th className="py-2 pr-3 text-right">Costo</th><th className="py-2 pr-3 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {capas.length === 0 && !isFetching && (
                    <tr><td colSpan={9} className="py-10 text-center text-muted-foreground">
                      Sin capas vivas de este producto en la bodega elegida.
                    </td></tr>)}
                  {capas.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pr-3">{new Date(c.fecha).toLocaleDateString(LOCALE)}</td>
                      <td className="py-2 pr-3">{c.bodega ?? '—'}</td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        {c.origen === 'saldo_inicial'
                          ? <span className="text-muted-foreground" title="Saldo que ya existía al activar las capas; no es una compra real">saldo inicial</span>
                          : (c.documento ?? '—')}
                      </td>
                      <td className="py-2 pr-3">{c.proveedor ?? '—'}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{c.numero_lote ?? '—'}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{num(c.unidades)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums font-medium">{num(c.restantes)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{num(c.costo_unitario, 4)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{money(c.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground">
            Las capas <strong>no cambian el método de valoración</strong>: el balance y el costo de ventas
            siguen usando promedio ponderado. Esto es el detalle que el promedio esconde.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Brecha: dónde el promedio se aleja más de la realidad
// ---------------------------------------------------------------------------
interface BrechaRow {
  producto_id: string; codigo: string; producto: string; location_id: string; bodega: string;
  unidades_capas: number; valor_capas: number; costo_capas: number;
  costo_promedio: number; valor_promedio: number; brecha: number;
  compras: number; costo_min: number; costo_max: number;
}

function Brecha() {
  const [loc, setLoc] = useState('');
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: rows = [], isFetching } = useQuery<BrechaRow[]>({
    queryKey: ['inv-capas-brecha', loc],
    queryFn: () => invRpc<BrechaRow[]>('inv_capas_brecha', { _location: loc || null, _minimo: 1 }),
  });
  const sort = useSort<BrechaRow>(rows, (r, k) => (r as unknown as Record<string, string | number | null>)[k] ?? null);
  const total = rows.reduce((a, r) => a + Number(r.brecha), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Campo label="Bodega" className="w-56">
          <select className={fieldCls} value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value="">Todas mis bodegas</option>
            {bodegas.filter((b) => b.es_activo).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </Campo>
        <p className="text-sm">
          Diferencia total: <strong className={`tabular-nums ${total > 0 ? 'text-emerald-600' : total < 0 ? 'text-rose-600' : ''}`}>{money(total)}</strong>
        </p>
      </div>

      <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
        Cuánto separa el valor del balance (promedio ponderado) de lo que realmente costó lo que queda en
        bodega. Una diferencia grande y persistente en un producto significa que su precio de compra se
        movió mucho y el promedio lo está disimulando — justo lo que hay que saber antes de fijar el PVP.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Donde más se separan ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="producto" sort={sort}>Producto</SortTh>
                <SortTh col="bodega" sort={sort}>Bodega</SortTh>
                <SortTh col="unidades_capas" sort={sort} align="right">Unidades</SortTh>
                <SortTh col="costo_promedio" sort={sort} align="right">Costo promedio</SortTh>
                <SortTh col="costo_capas" sort={sort} align="right">Costo real</SortTh>
                <th className="py-2 pr-3 text-right">Rango de compra</th>
                <SortTh col="brecha" sort={sort} align="right">Diferencia</SortTh>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !isFetching && (
                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">
                  El promedio coincide con lo que costó lo que queda. No hay nada escondido.
                </td></tr>)}
              {sort.sorted.map((r, i) => (
                <tr key={`${r.producto_id}-${r.location_id}`} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-xs text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs">{r.codigo}</span> {r.producto}</td>
                  <td className="py-2 pr-3">{r.bodega}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(r.unidades_capas)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(r.costo_promedio, 4)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium">{num(r.costo_capas, 4)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-xs text-muted-foreground">
                    {num(r.costo_min, 4)} – {num(r.costo_max, 4)}
                  </td>
                  <td className={`py-2 pr-3 text-right tabular-nums font-medium ${Number(r.brecha) > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {money(r.brecha)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pendientes de costear
//
// El guard del 810 impide que entre mercadería sin costo en silencio; esto es
// la otra mitad: lo que ya está dentro valorado en nada, para poder arreglarlo.
// ---------------------------------------------------------------------------
interface SinCosto {
  producto_id: string; codigo: string; nombre: string; location_id: string; bodega: string;
  unidades: number; costo_promedio: number; valorizado: number;
  costo_referencial: number | null; precio_base: number | null;
  desde: string | null; entradas_sin_costo: number;
}

function Pendientes() {
  const [loc, setLoc] = useState('');
  const [fijar, setFijar] = useState<SinCosto | null>(null);
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data, isFetching } = useQuery<{
    lineas: number; unidades: number; valor_estimado: number; detalle: SinCosto[];
  }>({
    queryKey: ['inv-sin-costo', loc],
    queryFn: () => invRpc('inv_sin_costo', { _location: loc || null }),
  });
  const rows = useMemo(() => data?.detalle ?? [], [data]);
  const sort = useSort<SinCosto>(rows, (r, k) => (r as unknown as Record<string, string | number | null>)[k] ?? null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Campo label="Bodega" className="w-56">
          <select className={fieldCls} value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value="">Todas mis bodegas</option>
            {bodegas.filter((b) => b.es_activo).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </Campo>
        {data && data.lineas > 0 && (
          <p className="text-sm text-muted-foreground">
            {num(data.unidades)} unidades hoy en cero · valdrían ≈{' '}
            <strong className="tabular-nums text-foreground">{money(data.valor_estimado)}</strong> con el costo de la ficha
          </p>
        )}
      </div>

      <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
        Mercadería en bodega cuyo costo es cero —o tan bajo frente a su precio de venta que da igual—.
        El balance no la cuenta, su venta reporta 100 % de margen y el costo de ventas nunca se registra.
        Y si entra más del mismo producto, ese cero arrastra hacia abajo el costo de todo lo que ya estaba.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Pendientes de costear ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="nombre" sort={sort}>Producto</SortTh>
                <SortTh col="bodega" sort={sort}>Bodega</SortTh>
                <SortTh col="unidades" sort={sort} align="right">Unidades</SortTh>
                <SortTh col="costo_promedio" sort={sort} align="right">Costo hoy</SortTh>
                <SortTh col="precio_base" sort={sort} align="right">Se vende a</SortTh>
                <SortTh col="costo_referencial" sort={sort} align="right">Costo de ficha</SortTh>
                <SortTh col="desde" sort={sort}>Desde</SortTh>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !isFetching && (
                <tr><td colSpan={9} className="py-10 text-center text-muted-foreground">
                  Nada valorado en cero. Todo el stock tiene su costo.
                </td></tr>)}
              {sort.sorted.map((r, i) => (
                <tr key={`${r.producto_id}-${r.location_id}`} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-xs text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs">{r.codigo}</span> {r.nombre}</td>
                  <td className="py-2 pr-3">{r.bodega}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(r.unidades)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-rose-600">{num(r.costo_promedio, 6)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{r.precio_base ? money(r.precio_base) : '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{r.costo_referencial ? num(r.costo_referencial, 4) : '—'}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">
                    {r.desde ?? '—'}{r.entradas_sin_costo > 0 && ` (${r.entradas_sin_costo})`}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setFijar(r)}>Costear</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {fijar && (
        <ModalCorregirCosto
          locationIdInicial={fijar.location_id}
          productoIdInicial={fijar.producto_id}
          onClose={() => setFijar(null)}
          onOk={() => setFijar(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
export function CostosInventario() {
  const [tab, setTab] = useState<'capas' | 'brecha' | 'pendientes'>('capas');
  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Calculator className="h-5 w-5" /> Costos del inventario
        </h1>
        <p className="text-sm text-muted-foreground">
          De qué compra salió cada unidad, cuánto separa eso del promedio, y qué hay valorado en nada.
        </p>
      </div>
      <div className="flex gap-1 border-b">
        {([['capas', 'Capas de costo'], ['brecha', 'Promedio vs. realidad'], ['pendientes', 'Pendientes de costear']] as const).map(([k, t]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm ${tab === k
              ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {k === 'capas' && <Layers className="h-3.5 w-3.5" />}{t}
          </button>
        ))}
      </div>
      {tab === 'capas' ? <Capas /> : tab === 'brecha' ? <Brecha /> : <Pendientes />}
    </div>
  );
}
